import React, { useMemo, useState } from "react";

import { useFetchCatalogInventoryHistoryQuery } from "../../../../api/api";
import {
  fill,
  formatDay,
  formatTime,
  getMovementLabelKey,
  getMovementSentenceKey,
  getMovementTone,
  getRelativeDayKey,
  getVariantLabel,
  groupMovementsByDay,
  humanizeMovementReference,
} from "../studioEditModel";
import { Btn, Card, EmptyState, Icon, Notice, Select, Spinner, cx } from "../StudioUi";

const PAGE_SIZES = [20, 50, 100];

const TONE_ICON = {
  in: "plus",
  out: "minus",
  sale: "cart",
  return: "undo",
  adjust: "sliders",
  undo: "undo",
};

const initials = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("") || "·";

/**
 * Only real data: inventory movements from GET inventory-history. The API
 * records no other product history, and this tab says so instead of
 * inventing a timeline.
 */
const HistoryTab = ({ studio }) => {
  const { tt, productId, workspace, locations } = studio;
  const [variantId, setVariantId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const limit = PAGE_SIZES[pageIndex];

  const { data, isFetching, isError, refetch } = useFetchCatalogInventoryHistoryQuery(
    {
      productId,
      params: {
        limit,
        ...(variantId ? { variantId: Number(variantId) } : {}),
        ...(locationId ? { locationId: Number(locationId) } : {}),
      },
    },
    { skip: !productId }
  );

  const items = useMemo(() => data?.items || [], [data?.items]);
  const total = Number(data?.total || 0);
  const variantById = useMemo(
    () => new Map((workspace.variants || []).map((variant) => [variant.id, variant])),
    [workspace.variants]
  );
  const presentTypes = useMemo(
    () => Array.from(new Set(items.map((item) => String(item.movementType || "").toLowerCase()))),
    [items]
  );
  const visible = typeFilter ? items.filter((item) => String(item.movementType).toLowerCase() === typeFilter) : items;
  const groups = groupMovementsByDay(visible);
  const unitsIn = visible.reduce((sum, item) => sum + Math.max(0, Number(item.quantityDelta || 0)), 0);
  const unitsOut = visible.reduce((sum, item) => sum + Math.abs(Math.min(0, Number(item.quantityDelta || 0))), 0);
  const hasFilters = Boolean(variantId || locationId || typeFilter);
  const canLoadMore = total > items.length && pageIndex < PAGE_SIZES.length - 1;

  const resetPaging = () => setPageIndex(0);

  return (
    <div className="pse-layout">
      <div className="pse-layout__main">
        <Card title={tt("hsTitle")} subtitle={tt("hsCopy")}>
          <Notice tone="info" className="pse-notice--compact">
            {tt("hsScopeNote")}
          </Notice>

          {presentTypes.length > 1 ? (
            <div className="pse-filter-chips" role="group" aria-label={tt("hsFilters")}>
              <button
                type="button"
                className={cx("pse-filter-chip", !typeFilter && "is-active")}
                aria-pressed={!typeFilter}
                onClick={() => setTypeFilter("")}
              >
                {tt("hsAllTypes")}
              </button>
              {presentTypes.map((type) => (
                <button
                  key={type}
                  type="button"
                  className={cx("pse-filter-chip", typeFilter === type && "is-active")}
                  aria-pressed={typeFilter === type}
                  onClick={() => setTypeFilter(type)}
                >
                  {tt(getMovementLabelKey(type))}
                </button>
              ))}
            </div>
          ) : null}

          {isError ? (
            <EmptyState
              icon="alert"
              title={tt("hsError")}
              action={
                <Btn variant="secondary" icon="refresh" onClick={() => refetch()}>
                  {tt("retry")}
                </Btn>
              }
            />
          ) : isFetching && !items.length ? (
            <div className="pse-center">
              <Spinner label={tt("loading")} />
            </div>
          ) : !visible.length ? (
            <EmptyState icon="history" title={hasFilters ? tt("hsEmptyFiltered") : tt("hsEmpty")} />
          ) : (
            <div className="pse-timeline">
              {groups.map((group) => {
                const relative = getRelativeDayKey(group.date);
                return (
                  <section key={group.key} className="pse-timeline__day">
                    <h4 className="pse-timeline__date">
                      {relative ? `${tt(relative)} · ` : ""}
                      {formatDay(group.date)}
                    </h4>
                    <ol className="pse-timeline__list">
                      {group.items.map((item) => {
                        const delta = Number(item.quantityDelta || 0);
                        const tone = getMovementTone(item.movementType, delta);
                        const variant = variantById.get(item.variantId);
                        const actor = item.actorName || tt("systemActor");
                        const reference = humanizeMovementReference(item.sourceReferenceId, tt);
                        return (
                          <li key={item.id} className="pse-timeline__item">
                            <span className="pse-timeline__time">{formatTime(item.createdAt)}</span>
                            <span className={cx("pse-timeline__icon", `is-${tone}`)}>
                              <Icon name={TONE_ICON[tone] || "cube"} size={16} strokeWidth={2.2} />
                            </span>
                            <span className="pse-avatar" aria-hidden="true">
                              {item.actorName ? initials(item.actorName) : <Icon name="user" size={14} />}
                            </span>
                            <div className="pse-timeline__body">
                              <strong>
                                {fill(tt(getMovementSentenceKey(item.movementType, delta)), {
                                  actor,
                                  count: Math.abs(delta),
                                })}
                                <span className="pse-timeline__type"> · {tt(getMovementLabelKey(item.movementType))}</span>
                              </strong>
                              <span className="pse-timeline__meta">
                                {(variant ? getVariantLabel(variant) : item.variantTitle) || "—"}
                                {" · "}
                                <Icon name="pin" size={12} />{" "}
                                {item.locationName || fill(tt("locationFallback"), { id: item.locationId })}
                                {item.quantityBefore !== null && item.quantityAfter !== null ? (
                                  <>
                                    {" · "}
                                    {fill(tt("hsFrom"), { before: item.quantityBefore, after: item.quantityAfter })}
                                  </>
                                ) : null}
                              </span>
                              {item.note ? <span className="pse-timeline__note">“{item.note}”</span> : null}
                              {reference ? (
                                <span className="pse-timeline__ref">{fill(tt("hsReference"), { ref: reference })}</span>
                              ) : null}
                            </div>
                            <span className={cx("pse-delta", delta >= 0 ? "is-in" : "is-out")}>
                              {delta > 0 ? "+" : ""}
                              {delta}
                            </span>
                          </li>
                        );
                      })}
                    </ol>
                  </section>
                );
              })}
            </div>
          )}

          {items.length ? (
            <div className="pse-table-foot pse-table-foot--row">
              <span>{fill(tt("hsShowing"), { shown: items.length, total })}</span>
              {canLoadMore ? (
                <Btn variant="secondary" size="sm" onClick={() => setPageIndex((index) => index + 1)} disabled={isFetching}>
                  {tt("hsLoadMore")}
                </Btn>
              ) : total > items.length ? (
                <span className="pse-muted pse-small">{tt("hsMaxReached")}</span>
              ) : null}
            </div>
          ) : null}
        </Card>
      </div>

      <aside className="pse-layout__side">
        <Card title={tt("hsSummary")} subtitle={tt("hsSummaryCopy")}>
          <div className="pse-mini-stats pse-mini-stats--3">
            <div className="pse-mini-stat">
              <strong>{visible.length}</strong>
              <span>{tt("hsMoves")}</span>
            </div>
            <div className="pse-mini-stat pse-mini-stat--success">
              <strong>+{unitsIn}</strong>
              <span>{tt("hsUnitsIn")}</span>
            </div>
            <div className="pse-mini-stat pse-mini-stat--danger">
              <strong>−{unitsOut}</strong>
              <span>{tt("hsUnitsOut")}</span>
            </div>
          </div>
        </Card>

        <Card title={tt("hsFilters")}>
          <div className="pse-stack">
            <label className="pse-field__label" htmlFor="pse-history-variant">
              {tt("hsVariant")}
            </label>
            <Select
              id="pse-history-variant"
              value={variantId}
              onChange={(value) => {
                setVariantId(value);
                resetPaging();
              }}
            >
              <option value="">{tt("hsAllVariants")}</option>
              {(workspace.variants || []).map((variant) => (
                <option key={variant.id} value={variant.id}>
                  {getVariantLabel(variant) || fill(tt("variantFallback"), { id: variant.id })}
                </option>
              ))}
            </Select>
            <label className="pse-field__label" htmlFor="pse-history-location">
              {tt("hsLocation")}
            </label>
            <Select
              id="pse-history-location"
              value={locationId}
              onChange={(value) => {
                setLocationId(value);
                resetPaging();
              }}
            >
              <option value="">{tt("skAllLocations")}</option>
              {(locations || []).map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </Select>
            {hasFilters ? (
              <Btn
                variant="ghost"
                size="sm"
                onClick={() => {
                  setVariantId("");
                  setLocationId("");
                  setTypeFilter("");
                  resetPaging();
                }}
              >
                {tt("vrResetFilters")}
              </Btn>
            ) : null}
          </div>
        </Card>
      </aside>
    </div>
  );
};

export default HistoryTab;
